import { Amplify } from "aws-amplify";
import { Navigate, Outlet, Route, Routes, useLocation, useParams, BrowserRouter } from "react-router-dom";
import "@frontend/App.css";
import AppErrorBoundary from "@frontend/components/AppErrorBoundary/AppErrorBoundary";
import Header from "@frontend/components/Header/Header";
import { LearningWorkspacePage } from "@frontend/pages/learning-workspace/LearningWorkspacePage";
import { LearningSidebar } from "./components/LearningSidebar";
import { parseModuleOrder, resolveModuleAssignments } from "@frontend/pages/learning-workspace/moduleOrder";
import { awsConfig, config } from "./config";
import { learningWorkspace } from "./learning";
import { ChatSessionsContextProvider } from "@frontend/lib/contexts/ChatSessionsProvider";
import { CustomThemeContextProvider } from "@frontend/lib/contexts/ThemeProvider";
import { UserInfoContextProvider } from "@frontend/lib/contexts/UserInfoProvider";
import { useUserInfoContext } from "@frontend/lib/contexts/UserInfoContext";
import { Auth } from "@frontend/pages/auth/auth";
import { isLincolnAuthorized } from "@common/domain/auth";
import Unauthorized from "@frontend/pages/unauthorized/unauthorized";
import TextChatPage from "@frontend/pages/text-chat/TextChatPage";
import ConversationResultsPage from "@frontend/pages/conversation-results/ConversationResultsPage";
import TranscriptsTab from "@frontend/pages/admin/TranscriptsTab";
import ErrorsTab from "@frontend/pages/admin/ErrorsTab";
import { AdminAuth } from "@frontend/components/Auth/AdminAuth";
import { ConversationResultsAuth } from "@frontend/components/Auth/ConversationResultsAuth";
import { SuperAdminAuth } from "@frontend/components/Auth/SuperAdminAuth";
import { Box, Typography, Container, Paper, Grid, Card, CardContent } from "@mui/material";

if (learningWorkspace.enabled && !config.learningPersonaId.trim()) {
  throw new Error('Learning Workspace requires a resolved persona ID when enabled');
}

Amplify.configure({
  Auth: {
    region: awsConfig.REGION,
    userPoolId: awsConfig.USER_POOL_ID,
    userPoolWebClientId: awsConfig.USER_POOL_APP_CLIENT_ID,
    mandatorySignIn: true,
    authenticationFlowType: "USER_PASSWORD_AUTH",
    oauth: {
      domain: awsConfig.AUTH_DOMAIN,
      scope: ["email", "openid"],
      redirectSignIn: awsConfig.WEB_URL,
      redirectSignOut: awsConfig.WEB_URL,
      responseType: "code",
    },
  },
});

// "About Lincoln" - 1838 historical context page
const AboutLincoln = () => {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: "1px solid var(--glass-border-subtle)", background: "var(--color-surface)" }}>
        <Typography variant="h3" sx={{ fontFamily: "'Newsreader', Georgia, serif", mb: 2, fontWeight: 500, letterSpacing: "-0.02em" }}>
          Abraham Lincoln in 1838
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4, fontStyle: "italic" }}>
          "Let reverence for the laws, be breathed by every American mother, to the lisping babe, that prattles on her lap..." — The Lyceum Address, January 27, 1838
        </Typography>

        <Box sx={{ display: "grid", gap: 3, fontSize: "1.1rem", lineHeight: 1.6, color: "text.primary" }}>
          <Typography>
            In the year 1838, Abraham Lincoln is a twenty-nine-year-old junior law partner in Springfield, Illinois. Admitted to the Illinois bar only a year prior, he is beginning to ride the Eighth Judicial Circuit and has established a partnership with John Todd Stuart. He is also a prominent young Whig representative in the Illinois General Assembly.
          </Typography>
          <Typography>
            This era represents Lincoln's formative professional and political emergence. Having recently relocated from the rustic frontier village of New Salem to the growing state capital of Springfield, he is honing his skill as an attorney and developing his distinctive philosophy on law, citizenship, and the preservation of free institutions.
          </Typography>
          <Typography>
            On January 27, 1838, Lincoln delivered one of his earliest published speeches, the Lyceum Address, to the Young Men's Lyceum of Springfield. Speaking on "The Perpetuation of Our Political Institutions," he warned against the rise of mob lawlessness and argued that a deep, abiding reverence for the Constitution and the law is the only true safeguard of American liberty.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

// "Activities to Try" - 1838 historical prompts
const ActivitiesToTry = () => {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h3" sx={{ fontFamily: "'Newsreader', Georgia, serif", mb: 2, fontWeight: 500, letterSpacing: "-0.02em" }}>
        Activities to Try
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Engage with young attorney Abraham Lincoln on the Illinois frontier using these suggested conversation prompts.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ height: "100%", borderRadius: 3, background: "var(--color-surface)" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                📜 Discuss the Lyceum Address of 1838
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ask Lincoln about his recent address on the "Perpetuation of Our Political Institutions." Inquire about his warnings on mob rule, lawlessness, and his vision of making reverence for the laws the "political religion of the nation."
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ height: "100%", borderRadius: 3, background: "var(--color-surface)" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                📚 Learn About Frontier Self-Education
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ask Lincoln how he managed to educate himself and read law on the frontier. Discuss his experience reading Blackstone's Commentaries, studying grammar from Samuel Kirkham's book, and learning Euclidean geometry.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ height: "100%", borderRadius: 3, background: "var(--color-surface)" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                ⚖️ Talk Law with a Springfield Lawyer
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ask him about his newly formed legal partnership with John Todd Stuart, riding the Eighth Judicial Circuit on horseback, or what type of civil and property cases a young Illinois lawyer handles.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ height: "100%", borderRadius: 3, background: "var(--color-surface)" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                ✉️ Inquire About New Salem Frontier Life
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Discuss his early days in New Salem working as a store clerk, postmaster, and deputy surveyor. Ask him about the origins of his nickname "Honest Abe" or his memories of the Black Hawk War.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

const NavigateToTextChat = () => {
  const { urlChatSession } = useParams();
  return <Navigate to={`/text-chat/${urlChatSession}`} replace />;
};

const NavigateToLearningWorkspace = ({ modality }: { modality: 'textbook' | 'chat' | 'avatar' }) => {
  const { chapterSlug } = useParams<{ chapterSlug?: string }>();
  const chapterPath = chapterSlug ? `/${chapterSlug}` : '';
  return <Navigate to={`/learn/${learningWorkspace.persona.slug}/${modality}${chapterPath}`} replace />;
};

const NavigateToLearningHome = () => {
  const { userInfo } = useUserInfoContext();

  if (!learningWorkspace.enabled || learningWorkspace.materials.length === 0) {
    return <Navigate to="/text-chat" replace />;
  }

  const [firstAssignment] = resolveModuleAssignments(
    learningWorkspace.materials,
    parseModuleOrder(userInfo?.moduleOrder),
  );
  if (!firstAssignment) {
    return <Navigate to="/text-chat" replace />;
  }
  return (
    <Navigate
      to={`/learn/${learningWorkspace.persona.slug}/${firstAssignment.modality}/${firstAssignment.chapterSlug}`}
      replace
    />
  );
};

const LincolnLayout = () => {
  const { pathname } = useLocation();
  const showSidebar = pathname.startsWith('/learn/') || pathname === '/admin' || pathname.startsWith('/admin/');
  const isWorkspaceRoute = pathname.startsWith('/learn/');

  return (
    <Box sx={{ display: "flex", minHeight: 0 }}>
      {showSidebar && <LearningSidebar workspace={learningWorkspace} />}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          height: "calc(100dvh - 48px)",
          overflowY: isWorkspaceRoute ? "auto" : "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

const LearningWorkspaceRoute = () => {
  const params = useParams<{
    personaSlug?: string;
    modality?: string;
    chapterSlug?: string;
  }>();

  if (
    params.personaSlug !== learningWorkspace.persona.slug ||
    !params.modality ||
    !['textbook', 'chat', 'avatar'].includes(params.modality)
  ) {
    return <NavigateToLearningHome />;
  }

  return (
    <LearningWorkspacePage
      personaId={config.learningPersonaId}
      workspace={learningWorkspace}
    />
  );
};

export const Root = () => {
  return (
    <div>
      <Routes>
        <Route element={<CustomThemeContextProvider />}>
          <Route element={<UserInfoContextProvider />}>
            <Route element={<ChatSessionsContextProvider />}>
              <Route element={<Header />}>
                <Route element={<Auth showGoogleSignIn={true} isAuthorized={isLincolnAuthorized} />}>
                  <Route element={<LincolnLayout />}>
                    {/* Root "/" goes to the default chat page */}
                    <Route path="/" element={<Navigate to="/text-chat" replace />} />                    
                    <Route path="/chat" element={<Navigate to="/text-chat" replace />} />
                    <Route path="/chat/:urlChatSession" element={<NavigateToTextChat />} />
                    <Route path="/text-chat" element={<TextChatPage />} />
                    <Route path="/text-chat/:urlChatSession" element={<TextChatPage />} />
                    {/* Legacy links to the top-level paths */}
                    <Route path="/about" element={<Navigate to="/learn/about" replace />} />
                    <Route path="/activities" element={<Navigate to="/learn/activities" replace />} />
                    <Route path="/learn/about" element={<AboutLincoln />} />
                    <Route path="/learn/activities" element={<ActivitiesToTry />} />
                    <Route path="/learn" element={<NavigateToLearningHome />} />
                    <Route path="/learn/:personaSlug" element={<NavigateToLearningHome />} />
                    <Route path="/learn/textbook/:chapterSlug?" element={<NavigateToLearningWorkspace modality="textbook" />} />
                    <Route path="/learn/chat/:chapterSlug?" element={<NavigateToLearningWorkspace modality="chat" />} />
                    <Route path="/learn/avatar/:chapterSlug?" element={<NavigateToLearningWorkspace modality="avatar" />} />
                    <Route
                      path="/learn/:personaSlug/:modality/:chapterSlug?"
                      element={<LearningWorkspaceRoute />}
                    />
                    <Route path="/learn/*" element={<NavigateToLearningHome />} />
                    <Route element={<AdminAuth />}>
                      <Route path="/admin" element={<Navigate to="/admin/conversation-results" replace />} />
                      <Route path="/admin/transcripts" element={<TranscriptsTab defaultPersonaId={config.learningPersonaId} />} />
                    </Route>
                    <Route element={<ConversationResultsAuth />}>
                      <Route
                        path="/admin/conversation-results"
                        element={
                          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
                            <ConversationResultsPage />
                          </Box>
                        }
                      />
                    </Route>
                    <Route element={<SuperAdminAuth />}>
                      <Route path="/admin/errors" element={<ErrorsTab />} />
                    </Route>
                  </Route>
                </Route>
                <Route path="/unauthorized" element={<Unauthorized />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Routes>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppErrorBoundary>
        <Root />
      </AppErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
